import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Plane, Navigation } from 'lucide-react';

const FleetPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Aircraft' },
    { id: 'gulfstream', name: 'Gulfstream' },
    { id: 'bombardier', name: 'Bombardier' },
    { id: 'other', name: 'Embraer / Hawker / Citation' },
    { id: 'large', name: 'Large Aircraft' },
  ];

  const aircraft = [
    // Gulfstream Series
    {
      id: 'g100',
      name: 'Gulfstream G100',
      category: 'gulfstream',
      capacity: '6 passengers',
      range: 'Short',
      description: 'Efficient light jet ideal for short to medium-range executive travel with impressive speed and comfort.',
      image: 'https://latitude2009.com/images/thumbs/fleet/gulfstream/gulfstream-gallery-jet-runway.webp',
    },
    {
      id: 'g150',
      name: 'Gulfstream G150',
      category: 'gulfstream',
      capacity: '6-8 passengers',
      range: 'Short',
      description: 'Super-light jet offering excellent range and cabin comfort for regional business travel.',
      image: 'https://images.aircharterservice.com/global/aircraft-guide/private-charter/gulfstream-g150-1.jpg',
    },
    {
      id: 'g200',
      name: 'Gulfstream G200',
      category: 'gulfstream',
      capacity: '9 passengers',
      range: 'Mid',
      description: 'Super mid-size jet with stand-up cabin and transcontinental range capabilities.',
      image: 'https://files.fusion-jet.com/9e2p426tl513/Gulfstream%20G200%20exterior%201.jpg',
    },
    {
      id: 'g450',
      name: 'Gulfstream G450',
      category: 'gulfstream',
      capacity: '12-13 passengers',
      range: 'Long',
      description: 'Large-cabin, long-range aircraft perfect for intercontinental executive travel.',
      image: 'https://images.prismic.io/flexjet-marketing/aRdSt7pReVYa4fxs_post-1232-featured-image.jpg?auto=format,compress',
    },
    {
      id: 'g550',
      name: 'Gulfstream G550',
      category: 'gulfstream',
      capacity: '12-16 passengers',
      range: 'Ultra Long',
      description: 'Ultra-long-range flagship with exceptional cabin space and worldwide nonstop capability.',
      image: 'https://cdn.privatejetcardcomparisons.com/uploads/Gulfstream-G550-pric1e.jpg',
    },
    {
      id: 'g650er',
      name: 'Gulfstream G650ER',
      category: 'gulfstream',
      capacity: '12-16 passengers',
      range: 'Ultra Long',
      description: 'Industry-leading ultra-long-range jet with unmatched speed, range, and luxury.',
      image: 'https://www.duncanaviation.aero/contentAsset/image/89c53328-fefb-4cd1-8675-cd554b32b625',
    },

    // Bombardier Series
    {
      id: 'global5000',
      name: 'Global 5000',
      category: 'bombardier',
      capacity: '10-13 passengers',
      range: 'Long',
      description: 'Large-cabin business jet offering exceptional comfort and intercontinental range.',
      image: 'https://images.aircharterservice.com/global/aircraft-guide/private-charter/bombardier-global-5000.jpg',
    },
    {
      id: 'global6000',
      name: 'Global 6000',
      category: 'bombardier',
      capacity: '12-14 passengers',
      range: 'Ultra Long',
      description: 'Ultra-long-range aircraft with industry-leading cabin altitude and smooth ride quality.',
      image: 'https://www.eyerevolution.co.uk/wp-content/uploads/2014/07/global-6000-opt.jpg',
    },
    {
      id: 'challenger350',
      name: 'Challenger 350',
      category: 'bombardier',
      capacity: '8 passengers',
      range: 'Mid',
      description: 'Super mid-size jet with widest cabin in class and impressive coast-to-coast range.',
      image: 'https://monarchairgroup.com/wp-content/uploads/2019/07/challenger-350.jpg',
    },
    {
      id: 'challenger605',
      name: 'Challenger 605',
      category: 'bombardier',
      capacity: '10-12 passengers',
      range: 'Long',
      description: 'Large-cabin jet combining comfort, performance, and reliability for demanding travelers.',
      image: 'https://execaireaviation.com/wp-content/uploads/2024/06/8-copy-2-scaled.jpg',
    },
    {
      id: 'challenger850',
      name: 'Challenger 850',
      category: 'bombardier',
      capacity: '12-14 passengers',
      range: 'Long',
      description: 'Corporate shuttle with airline-style cabin and exceptional range for group travel.',
      image: 'https://jetservicenl.com/files/aircraft/challenger-850.1.jpg',
    },

    // Embraer / Hawker / Citation
    {
      id: 'phenom300er',
      name: 'Phenom 300ER',
      category: 'other',
      capacity: '6 passengers',
      range: 'Light',
      description: 'Best-selling light jet with impressive performance and one of the largest cabins in its class.',
      image: 'https://worldwidelux.com/2.0/wp-content/uploads/2025/03/world_wide_lux_fleet_embraer_phenom-300_1.jpg',
    },
    {
      id: 'hawker850xp',
      name: 'Hawker 850XP',
      category: 'other',
      capacity: '6-8 passengers',
      range: 'Mid',
      description: 'The Hawker 850XP is a reliable mid-size jet offering excellent comfort and range.',
      image: 'https://www.voluxis.com/wp-content/uploads/2025/01/Voluxis-Hawker-6-scaled.jpg',
    },
    {
      id: 'citationx',
      name: 'Citation X',
      category: 'other',
      capacity: '8 passengers',
      range: 'Mid',
      description: 'Fastest civilian aircraft with Mach 0.935 speed capability for time-critical missions.',
      image: 'https://monarchairgroup.com/wp-content/uploads/2016/11/citation-x-plus-charter.jpg',
    },

    // Large Aircraft
    {
      id: 'boeing737',
      name: 'Boeing 737',
      category: 'large',
      capacity: '19-104 passengers',
      range: 'Long',
      description: 'Large aircraft ideal for group charter, sports teams, and corporate events requiring significant capacity.',
      image: '/bbj.jpg',
    },
  ];

  const filteredAircraft = activeCategory === 'all' 
    ? aircraft 
    : aircraft.filter(a => a.category === activeCategory);

  const getRangeColor = (range) => {
    switch (range) {
      case 'Light': return 'bg-emerald-100 text-emerald-800';
      case 'Mid': return 'bg-blue-100 text-blue-800';
      case 'Long': return 'bg-purple-100 text-purple-800';
      case 'Ultra Long': return 'bg-amber-100 text-amber-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <div data-testid="fleet-page" className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-start overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=85"
            alt="Private jet wing view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 container-padding w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-blue-700 text-sm font-medium tracking-widest uppercase mb-6">
              On-Demand Fleet
            </span>
            <h1 
              data-testid="fleet-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Our On-Demand Fleet
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl">
              Access to over 500 premium aircraft worldwide, from light jets to wide-body airliners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-slate-50 border-b border-slate-200 sticky top-20 md:top-24 z-40">
        <div className="container-padding">
          <div className="flex flex-wrap gap-2 md:gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                data-testid={`filter-${category.id}`}
                className={`px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm font-medium tracking-wider uppercase transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Aircraft Grid */}
      <section className="section-gap">
        <div className="container-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAircraft.map((plane, index) => (
              <motion.div
                key={plane.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                data-testid={`aircraft-card-${plane.id}`}
                className="card-fleet group"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={plane.image}
                    alt={plane.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Range Badge */}
                  <span className={`absolute top-4 right-4 px-3 py-1 text-xs font-medium ${getRangeColor(plane.range)}`}>
                    {plane.range} Range
                  </span>
                </div>

                {/* Content */}
                <div className="p-6 bg-white">
                  <h3 
                    className="text-xl font-medium text-slate-900 mb-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {plane.name}
                  </h3>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1 text-slate-500 text-sm">
                      <Users className="w-4 h-4" />
                      <span>{plane.capacity}</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-500 text-sm">
                      <Navigation className="w-4 h-4" />
                      <span>{plane.range}</span>
                    </div>
                  </div>

                  <p className="text-slate-600 text-sm leading-relaxed">
                    {plane.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Categories Overview */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">Aircraft Categories</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Choose Your Perfect Aircraft
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { range: 'Light', desc: 'Ideal for trips up to 3 hours. Efficient and economical.', icon: <Plane className="w-8 h-8" /> },
              { range: 'Mid', desc: 'Perfect for regional travel. Balance of range and comfort.', icon: <Plane className="w-8 h-8" /> },
              { range: 'Long', desc: 'Intercontinental capability with premium cabin space.', icon: <Plane className="w-8 h-8" /> },
              { range: 'Ultra Long', desc: 'Non-stop worldwide travel with ultimate luxury.', icon: <Plane className="w-8 h-8" /> },
            ].map((cat, index) => (
              <motion.div
                key={cat.range}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 border border-slate-100 text-center"
              >
                <div className="text-blue-700 mb-4 flex justify-center">{cat.icon}</div>
                <h3 
                  className="text-xl font-medium text-slate-900 mb-3"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {cat.range} Range
                </h3>
                <p className="text-slate-600 text-sm">{cat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-gap">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 
              className="heading-lg text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Can't Find What You Need?
            </h2>
            <p className="body-large mb-10 max-w-2xl mx-auto">
              Our extensive network provides access to virtually any aircraft type. Contact us with your requirements.
            </p>
            <Link
              to="/contact"
              data-testid="fleet-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
            >
              Request a Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FleetPage;
